import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const INDIAN_STATES = [
  "Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra",
  "Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand",
  "West Bengal","Other",
];

export const RegistrationForm = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", city: "", state: "", pincode: "", interest: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.city || !form.state || !form.pincode) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Registration successful! Our team will call you within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="register" data-testid="register-section" className="section-pad bg-[#FAFAF9]">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C2410C]">Register free</div>
          <h2 className="font-display mt-3 text-3xl sm:text-5xl font-black text-[#064E3B] tracking-tight">
            Take the first step today
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#44403C] max-w-md">
            Fill this form. Our regional manager will call you within 24 hours to explain the starter kit
            and pricing. <b>No fee, no commitment.</b>
          </p>
          <ul className="mt-6 space-y-3 text-sm text-[#1C1917]">
            {[
              "Personal one-on-one onboarding call",
              "Starter kit delivered to your doorstep",
              "WhatsApp support group with 500+ distributors",
              "Reorder anytime — no minimum quantity",
            ].map((li) => (
              <li key={li} className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-[#064E3B] mt-0.5" /> {li}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="register-success"
              className="bg-white rounded-3xl p-10 border-2 border-[#064E3B] text-center"
            >
              <div className="h-16 w-16 rounded-full bg-[#064E3B] flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-9 w-9 text-[#F59E0B]" />
              </div>
              <h3 className="font-display mt-6 text-3xl font-black text-[#064E3B]">You're in!</h3>
              <p className="mt-3 text-[#44403C]">
                Our team will call you on <b>{form.phone}</b> within 24 hours.
              </p>
              <button
                data-testid="register-again"
                onClick={() => { setDone(false); setForm({ name:"", phone:"", email:"", city:"", state:"", pincode:"", interest:"" }); }}
                className="pill-btn pill-btn-ghost mt-8"
              >
                Register another person
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="register-form"
              className="bg-white rounded-3xl p-7 sm:p-10 border border-[#E7E5E4] space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full name*" testid="input-name">
                  <input required value={form.name} onChange={upd("name")} data-testid="reg-name" className={inputCls} placeholder="e.g. Priya Sharma"/>
                </Field>
                <Field label="Phone (WhatsApp)*" testid="input-phone">
                  <input required value={form.phone} onChange={upd("phone")} data-testid="reg-phone" className={inputCls} placeholder="10-digit mobile" inputMode="tel"/>
                </Field>
                <Field label="Email*" testid="input-email">
                  <input required type="email" value={form.email} onChange={upd("email")} data-testid="reg-email" className={inputCls} placeholder="you@email.com"/>
                </Field>
                <Field label="City*" testid="input-city">
                  <input required value={form.city} onChange={upd("city")} data-testid="reg-city" className={inputCls} placeholder="e.g. Jaipur"/>
                </Field>
                <Field label="State*" testid="input-state">
                  <select required value={form.state} onChange={upd("state")} data-testid="reg-state" className={inputCls}>
                    <option value="">Select state</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Pincode*" testid="input-pincode">
                  <input required value={form.pincode} onChange={upd("pincode")} data-testid="reg-pincode" className={inputCls} placeholder="6-digit pincode" inputMode="numeric"/>
                </Field>
              </div>

              <Field label="Why do you want to start? (optional)" testid="input-interest">
                <textarea rows={3} value={form.interest} onChange={upd("interest")} data-testid="reg-interest" className={inputCls} placeholder="Tell us a little about your goals..."/>
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="reg-submit"
                className="pill-btn pill-btn-primary w-full justify-center disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
                {loading ? "Submitting..." : "Submit my registration"}
              </button>
              <p className="text-xs text-[#78716C] text-center">
                By registering you agree to be contacted by our team. Your details stay private.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const inputCls = "w-full h-12 rounded-xl border border-[#E7E5E4] bg-white px-4 text-sm font-medium text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#57534E]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default RegistrationForm;
